/*
  AMAVO CLINICAL — product page behaviour
  ------------------------------------------------------------------
  Six custom elements. Loaded only on the product template, with defer.
  Each one enhances markup that is already correct without it.
*/

(() => {
  'use strict';

  /*
    Doua sectiuni de pe fisa produsului cer acelasi fisier (main-product si
    recently-viewed), iar browserul executa de doua ori un <script src>
    repetat in pagina. Elementele custom sunt aparate de `customElements.get`,
    dar ascultatorii de pe document nu erau: fiecare clic pe plus urca doi pasi.
  */
  if (window.amvProductLoaded) return;
  window.amvProductLoaded = true;

  const money = (cents) => {
    const fmt = (window.amavo && window.amavo.moneyFormat) || '{{amount}} lei';
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, value);
  };

  /* ---------------------------------------------------------------
     GALLERY
     The track is a scroll container, so swipe already works. This adds
     thumbnails, dots and the lightbox, and keeps all three in sync from
     one source of truth: the track's scroll position.
     --------------------------------------------------------------- */

  class AmvGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-amv-gallery-track]');
      if (!this.track) return;

      this.items = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('[data-amv-gallery-thumb]'));
      this.dots = Array.from(this.querySelectorAll('.amv-gallery__dot'));

      [...this.thumbs, ...this.dots].forEach((btn) =>
        btn.addEventListener('click', () => this.go(Number(btn.dataset.index)))
      );

      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.sync();
          ticking = false;
        });
      });

      this.querySelectorAll('[data-amv-lightbox]').forEach((btn) =>
        btn.addEventListener('click', () => this.openLightbox(Number(btn.dataset.amvLightbox)))
      );

      this.sync();
    }

    go(index) {
      const item = this.items[index];
      if (!item) return;
      // scrollIntoView would also scroll the page; this only moves the track.
      this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.current = index;
      this.mark(index);
    }

    sync() {
      const x = this.track.scrollLeft;
      let best = 0;
      let min = Infinity;
      this.items.forEach((item, i) => {
        const d = Math.abs(item.offsetLeft - this.track.offsetLeft - x);
        if (d < min) { min = d; best = i; }
      });
      this.current = best;
      this.mark(best);
    }

    mark(index) {
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
      this.dots.forEach((d, i) => d.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    }

    /* Selecting a variant with its own image should move the gallery to
       that image rather than leaving the customer on the wrong shot. */
    showMedia(mediaId) {
      const item = this.items.find((el) => el.dataset.mediaId === String(mediaId));
      if (item) this.go(this.items.indexOf(item));
    }

    openLightbox(index) {
      const box = document.querySelector('[id^="amv-lightbox-"]');
      if (!box || typeof box.open !== 'function') return;
      box.open();
      const strip = box.querySelector('[data-amv-lightbox-track]');
      const img = strip && strip.children[index];
      if (img) strip.scrollTo({ left: img.offsetLeft, behavior: 'instant' in window ? 'instant' : 'auto' });
    }
  }

  /* ---------------------------------------------------------------
     QUANTITY
     --------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-amv-qty]');
    if (!btn) return;
    const input = btn.parentElement.querySelector('.amv-qty__input');
    if (!input) return;
    const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.amvQty));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------------------------------------------------------
     VARIANT SELECTS
     Updates the URL, the hidden variant id, the price and the gallery
     without a page load, and falls back to a normal form post if the
     variant data is missing.
     --------------------------------------------------------------- */

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-amv-variants]');
      if (!data) return;
      try {
        this.variants = JSON.parse(data.textContent);
      } catch (e) {
        return;
      }
      this.addEventListener('change', () => this.update());
    }

    get chosen() {
      return Array.from(this.querySelectorAll('input:checked')).map((i) => i.value);
    }

    update() {
      const wanted = this.chosen;
      const match = this.variants.find((v) =>
        wanted.every((value, i) => v.options[i] === value)
      );
      if (!match) return;

      const idInput = document.querySelector('[data-amv-variant-id]');
      if (idInput) {
        idInput.value = match.id;
        idInput.setAttribute('value', match.id);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);

      this.querySelectorAll('[data-amv-option-value]').forEach((el, i) => {
        if (wanted[i]) el.textContent = wanted[i];
      });

      if (match.featured_media) {
        const gallery = document.querySelector('amv-gallery');
        if (gallery && gallery.showMedia) gallery.showMedia(match.featured_media.id);
      }

      this.updatePrices(match);
      this.updateBuyButtons(match);
    }

    updatePrices(match) {
      const price = Number(match.price) || 0;
      const compare = Number(match.compare_at_price) || 0;
      const onSale = compare > price;
      document.querySelectorAll('[data-amv-product-price]').forEach((container) => {
        const root = container.querySelector('.amv-price');
        const now = container.querySelector('[data-amv-price-now]');
        const was = container.querySelector('[data-amv-price-was]');
        const label = container.querySelector('[data-amv-price-sale-label]');
        if (root) root.classList.toggle('amv-price--sale', onSale);
        if (now) now.textContent = money(price);
        if (was) {
          was.textContent = money(compare);
          was.hidden = !onSale;
        }
        if (label) label.hidden = !onSale;
      });
    }

    updateBuyButtons(match) {
      document.querySelectorAll('[data-amv-add], [data-amv-sticky-add]').forEach((button) => {
        button.disabled = !match.available;
        const label = button.querySelector('[data-amv-add-label]');
        if (label) label.textContent = match.available ? button.dataset.amvAddText : button.dataset.amvSoldOutText;
      });
    }
  }

  /* ---------------------------------------------------------------
     BUY
     Adds to the cart over fetch so the customer keeps their place on
     the page. If the request fails the message comes from Shopify, not
     from us, so it says what actually went wrong.
     --------------------------------------------------------------- */

  class AmvBuy extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-amv-add]');
      this.label = this.querySelector('[data-amv-add-label]');
      this.spinner = this.querySelector('.amv-buy__spinner');
      this.error = this.querySelector('[data-amv-buy-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }

    async submit(event) {
      if (!window.fetch || !window.amavo) return; // let the form post normally
      event.preventDefault();
      this.setLoading(true);
      this.showError('');

      try {
        const body = new FormData(this.form);
        body.append('sections_url', window.location.pathname);
        const res = await fetch(window.amavo.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message);

        document.dispatchEvent(new CustomEvent('amv:cart:added', { detail: data }));
        await this.refreshCart();
        this.flash();
      } catch (err) {
        this.showError(err.message || '');
      } finally {
        this.setLoading(false);
      }
    }

    async refreshCart() {
      const res = await fetch(`${window.amavo.routes.cart_url || '/cart'}.js`);
      if (!res.ok) return;
      const cart = await res.json();
      document.dispatchEvent(new CustomEvent('amv:cart:updated', { detail: cart }));
    }

    setLoading(on) {
      this.toggleAttribute('data-loading', on);
      if (this.spinner) this.spinner.hidden = !on;
      if (this.button) this.button.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    flash() {
      if (!this.label) return;
      const original = this.label.textContent;
      this.label.textContent = this.dataset.addedLabel || '✓';
      setTimeout(() => { this.label.textContent = original; }, 1600);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }

  /* ---------------------------------------------------------------
     FREE SHIPPING BAR
     --------------------------------------------------------------- */

  class AmvShipBar extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('[data-amv-shipbar-text]');
      this.fill = this.querySelector('[data-amv-shipbar-fill]');
      this.threshold = Number(this.dataset.threshold) || 0;
      document.addEventListener('amv:cart:updated', (e) => this.render(e.detail.total_price));
    }

    render(total) {
      if (!this.threshold) return;
      const pct = Math.min(100, Math.round((total / this.threshold) * 100));
      if (this.fill) this.fill.style.width = `${pct}%`;
      if (!this.text) return;
      const left = this.threshold - total;
      this.text.textContent =
        left > 0
          ? (this.dataset.remaining || 'Mai ai {{ amount }} până la transport gratuit').replace('{{ amount }}', money(left))
          : this.dataset.reached || 'Ai transport gratuit';
    }
  }

  /* ---------------------------------------------------------------
     CUTOFF COUNTDOWN
     Evaluated in the SHOP's timezone, because the warehouse is what
     ships. After the cutoff — and at weekends — it names the next
     working day instead of restarting a fresh countdown, which would
     be a fake urgency timer and a misleading practice under
     Directive 2005/29/EC.
     --------------------------------------------------------------- */

  class AmvCutoff extends HTMLElement {
    connectedCallback() {
      this.out = this.querySelector('[data-amv-cutoff-text]');
      this.hour = parseInt(this.dataset.cutoff, 10);
      if (!this.out || Number.isNaN(this.hour)) return;

      // "+0300" -> minutes east of UTC
      const raw = (this.dataset.offset || '').trim();
      const sign = raw.startsWith('-') ? -1 : 1;
      this.offsetMinutes =
        sign * (parseInt(raw.slice(1, 3), 10) * 60 + parseInt(raw.slice(3, 5), 10) || 0);

      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    shopNow() {
      const now = new Date();
      return new Date(now.getTime() + (this.offsetMinutes * 60 + now.getTimezoneOffset() * 60) * 1000);
    }

    tick() {
      const now = this.shopNow();
      const day = now.getDay(); // 0 Sun … 6 Sat
      const cutoff = new Date(now);
      cutoff.setHours(this.hour, 0, 0, 0);

      if (day === 6 || day === 0) {
        this.out.textContent = this.dataset.monday;
        clearInterval(this.timer);
        return;
      }

      if (now >= cutoff) {
        this.out.textContent = day === 5 ? this.dataset.monday : this.dataset.tomorrow;
        clearInterval(this.timer);
        return;
      }

      const left = cutoff - now;
      const h = Math.floor(left / 3600000);
      const m = Math.floor((left % 3600000) / 60000);
      const s = Math.floor((left % 60000) / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      const time = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
      this.out.textContent = (this.dataset.today || '').replace('__TIME__', time);
    }
  }

  /* ---------------------------------------------------------------
     RECENTLY VIEWED
     Stored locally, never sent anywhere — no consent banner needed for
     a purely local preference under GDPR recital 30 / ePrivacy, since
     nothing leaves the device.
     --------------------------------------------------------------- */

  const RECENT_KEY = 'amv:recent';

  function rememberProduct(handle) {
    if (!handle) return;
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (e) {
      /* private mode: not remembering is fine */
    }
  }

  const pdp = document.querySelector('[data-amv-product-handle]');
  if (pdp) rememberProduct(pdp.dataset.amvProductHandle);

  if (!customElements.get('amv-gallery')) customElements.define('amv-gallery', AmvGallery);
  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);
  if (!customElements.get('amv-buy')) customElements.define('amv-buy', AmvBuy);
  if (!customElements.get('amv-ship-bar')) customElements.define('amv-ship-bar', AmvShipBar);
  if (!customElements.get('amv-cutoff')) customElements.define('amv-cutoff', AmvCutoff);
})();

/*
  AMAVO CLINICAL — product page, part two
  ------------------------------------------------------------------
  Below-the-fold behaviour: recommendations, recently viewed, review
  sorting, the before/after slider and the sticky buy bar.
*/

(() => {
  'use strict';

  /* ---------------------------------------------------------------
     RECOMMENDATIONS
     Fetched after paint so the recommendation engine never sits in
     front of the product page.
     --------------------------------------------------------------- */

  class AmvRecs extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      const intent = this.dataset.intent;
      fetch(intent ? `${url}&intent=${intent}` : url)
        .then((r) => (r.ok ? r.text() : Promise.reject()))
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.querySelector('.amv-section');
          if (fresh) this.replaceWith(fresh);
        })
        .catch(() => this.remove());
    }
  }

  /* ---------------------------------------------------------------
     RECENTLY VIEWED
     Cards come from Liquid through the Section Rendering API, so they
     are the same markup as every other card on the site.
     --------------------------------------------------------------- */

  class AmvRecent extends HTMLElement {
    async connectedCallback() {
      const grid = this.querySelector('[data-amv-recent-grid]');
      if (!grid) return;

      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem('amv:recent') || '[]');
      } catch (e) {
        return;
      }

      handles = handles.filter((h) => h && h !== this.dataset.current)
                       .slice(0, Number(this.dataset.limit) || 4);
      if (!handles.length) return;

      const cards = await Promise.all(handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}?section_id=amv-card-fragment`);
          if (!res.ok) return null;
          const html = await res.text();
          const el = new DOMParser().parseFromString(html, 'text/html').querySelector('.amv-card');
          return el || null;
        } catch (e) {
          return null;
        }
      }));

      const found = cards.filter(Boolean);
      if (!found.length) return;
      found.forEach((c) => grid.appendChild(c));
      this.hidden = false;
    }
  }

  /* ---------------------------------------------------------------
     REVIEWS — sort, filter, show more
     Operates on the list already in the DOM, so it works on the first
     paint and needs no second request.
     --------------------------------------------------------------- */

  class AmvReviews extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-amv-rev-list]');
      if (!this.list) return;

      this.items = Array.from(this.list.children);
      this.perPage = Number(this.dataset.perPage) || 5;
      this.expanded = false;

      this.sort = this.querySelector('[data-amv-rev-sort]');
      this.skin = this.querySelector('[data-amv-rev-skin]');
      this.more = this.querySelector('[data-amv-rev-more]');

      if (this.sort) this.sort.addEventListener('change', () => this.render());
      if (this.skin) this.skin.addEventListener('change', () => this.render());
      if (this.more) this.more.addEventListener('click', () => { this.expanded = true; this.render(); });

      this.render();
    }

    render() {
      const skin = this.skin ? this.skin.value : '';
      const mode = this.sort ? this.sort.value : 'recent';

      const visible = this.items.filter((el) => !skin || el.dataset.skin === skin);

      visible.sort((a, b) => {
        if (mode === 'high') return Number(b.dataset.rating) - Number(a.dataset.rating);
        if (mode === 'low') return Number(a.dataset.rating) - Number(b.dataset.rating);
        return Number(b.dataset.date || 0) - Number(a.dataset.date || 0);
      });

      this.items.forEach((el) => { el.hidden = true; });
      const limit = this.expanded ? visible.length : this.perPage;
      visible.slice(0, limit).forEach((el) => {
        el.hidden = false;
        this.list.appendChild(el);
      });

      if (this.more) this.more.hidden = this.expanded || visible.length <= this.perPage;
    }
  }

  /* ---------------------------------------------------------------
     BEFORE / AFTER
     The control is a native range input, so keyboard, touch and screen
     readers are handled by the browser. This only moves the clip.
     --------------------------------------------------------------- */

  class AmvCompare extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-amv-compare-range]');
      this.clip = this.querySelector('[data-amv-compare-clip]');
      this.handle = this.querySelector('[data-amv-compare-handle]');
      if (!this.range || !this.clip) return;
      this.range.addEventListener('input', () => this.move());
      this.move();
    }

    move() {
      const pct = this.range.value;
      this.clip.style.width = `${pct}%`;
      if (this.handle) this.handle.style.left = `${pct}%`;
    }
  }

  /* ---------------------------------------------------------------
     STICKY BUY BAR
     Shown only once the real buy button has left the viewport, so it
     never competes with the button it duplicates.
     --------------------------------------------------------------- */

  class AmvSticky extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector('.amv-buy__add');
      if (!anchor || !('IntersectionObserver' in window)) return;

      if (this.dataset.desktop !== 'true') this.classList.add('amv-sticky--mobile');

      // Keep the sticky bar on whatever variant the main form holds.
      const source = document.querySelector('[data-amv-variant-id]');
      const target = this.querySelector('[data-amv-sticky-variant]');
      if (source && target) {
        const syncVariant = () => {
          target.value = source.value;
          target.setAttribute('value', source.value);
        };
        source.addEventListener('change', syncVariant);
        new MutationObserver(syncVariant)
          .observe(source, { attributes: true, attributeFilter: ['value'] });
      }

      new IntersectionObserver(
        ([entry]) => { this.hidden = entry.isIntersecting; },
        { rootMargin: '0px 0px -80px 0px' }
      ).observe(anchor);
    }
  }

  if (!customElements.get('amv-recs')) customElements.define('amv-recs', AmvRecs);
  if (!customElements.get('amv-recent')) customElements.define('amv-recent', AmvRecent);
  if (!customElements.get('amv-reviews')) customElements.define('amv-reviews', AmvReviews);
  if (!customElements.get('amv-compare')) customElements.define('amv-compare', AmvCompare);
  if (!customElements.get('amv-sticky')) customElements.define('amv-sticky', AmvSticky);
})();
